import { computed, ref, shallowRef } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import { cache, CacheClosedError, sectors } from "@/services/cache";
import { ContentfulSector, SectorMarketState } from "@tmi/types/entities";
import { createInitLatch } from "@/stores/init-latch";
import { createCatalogIndex, markRawRows } from "@/stores/catalog-index";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import type { DataSeries } from "@tmi/types/api/sector-rs-matrix";
import { refreshSectorsFromServer } from "@/services/data/sync/sector-sync-coordinator";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

const initialized = computed< boolean >( () => allSectors.value.length > 0 );
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
const contentfulRefreshing = ref< boolean >( false );
const iiRefreshing = ref< boolean >( false );
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();

const relativeStrengthLoading = ref< boolean >( false );
// Moves on every reset(): a server load a reset overtook (its 503 retry can take up to 10 s) must not land its sectors.
let loadGeneration: number = 0;

// Rank 11: a read-only catalog of raw rows, always replaced by assignment, looked up through Map indexes
// (SectorHistory hydration resolves one sector per sector-day).
const allSectors = shallowRef< ContentfulSector[] >( [] );
const sectorsByTmiId = createCatalogIndex( allSectors, ( sector: ContentfulSector ) => sector.id );
const sectorsByContentfulId = createCatalogIndex( allSectors, ( sector: ContentfulSector ) => sector.contentfulId );
const sectorsBySymbol = createCatalogIndex( allSectors, ( sector: ContentfulSector ) => sector.symbol );

const defaultRelativeStrengthHistory = [] as unknown as DataSeries[];
const rsHistory = ref< DataSeries[] >( defaultRelativeStrengthHistory );

const selectedSectorDatePoint = ref< SectorMarketState | undefined >();

export const useSectorsStore = defineStore( 'sectors', () => {
    const { authToken } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();

    function resetSectors(): void {
        allSectors.value = [];
        sectorsByTmiId.clear();
        sectorsByContentfulId.clear();
        sectorsBySymbol.clear();
    }

    function resetRelativeStrength(): void {
        rsHistory.value = defaultRelativeStrengthHistory;
        relativeStrengthLoading.value = false;
    }

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        loadGeneration += 1;
        resetSectors();
        resetRelativeStrength();

        loading.value = false;
        contentfulRefreshing.value = false;
        iiRefreshing.value = false;
        initLatch.clear();
    }

    async function initializeAsync(): Promise< void > {
        if ( initialized.value || loading.value || initLatch.isHalted() ) {
            logger.debug( '[ Sectors ].initialize() exiting - already processing...' );
            return;
        }

        loading.value = true;

        const t0: number = performance.now();

        // The cache read is wrapped so a rejected open (CacheClosedError — a newer build upgraded the
        // database and this tab is reloading) stops quietly instead of escaping initializeAsync() as an
        // unhandled rejection. The `else` branch keeps its own fire-and-forget settlement, so there is
        // deliberately no outer `finally` clearing the flags out from under it.
        try {
            // Wait for the database to open before the first read.
            await cache.ready();

            if ( !await sectors.isEmpty() ) {
                try {
                    const uniqueSectors = new Map< number, ContentfulSector >();

                    for ( const sector of markRawRows( await sectors.all() ) ) {
                        uniqueSectors.set( sector.id, sector );
                    }
                    allSectors.value = [ ...( Array.from( uniqueSectors.values() ) ) ]
                        .sort( ( s1: ContentfulSector, s2: ContentfulSector ) =>
                            s1.name.toLowerCase().localeCompare( s2.name.toLowerCase() )
                        );

                    const t1: number = performance.now();
                    logger.info( `(local) Loaded ${ allSectors.value.length } sectors in ${ t1 - t0 } ms` );
                } finally {
                    // Clear the "loading" flag
                    loading.value = false;
                }
            } else {
                if ( contentfulRefreshing.value ) {
                    return;
                }

                contentfulRefreshing.value = true;

                // Timing - testing to see how expensive each method is
                const t0: number = performance.now();

                const gen: number = loadGeneration;
                // tmi-p00: a content 503 is retried once; a second one keeps the placeholder sectors, as a 200 with
                // no CMS sectors does. Only the in-memory list is set here: the local cache is the worker's.
                refreshSectorsFromServer( authToken.value, { placeholdersWhileContentWarms: true } )
                    .then( ( hydratedSectors: ContentfulSector[] ) => {
                        if ( gen === loadGeneration ) {
                            allSectors.value = markRawRows( [ ...hydratedSectors ] );
                        }
                    } )
                    .catch( ( err: any ) => {
                        if ( gen !== loadGeneration ) return;
                        logger.error( JSON.stringify( err.message ) );
                        reset();
                    } )
                    .finally( () => {
                        if ( gen !== loadGeneration ) return;
                        // Clear the "loading" flag
                        loading.value = false;
                        contentfulRefreshing.value = false;

                        const t1: number = performance.now();
                        logger.info( `Fetching & mapping ${ allSectors.value.length } tmi/ii sector items took ${ t1 - t0 } ms` );
                    } );
            }
        } catch ( err: any ) {
            // A newer build upgraded the cache and this tab is reloading. The failure happened before
            // either branch started, so clearing `loading` here is safe; the latch keeps the store from
            // re-initializing until reset() (tmi-7be). The non-quiet path clears `loading` via reset().
            if ( err instanceof CacheClosedError ) { initLatch.halt( err ); loading.value = false; return; }
            logger.error( JSON.stringify( err.message ) );
            reset();
            initLatch.halt( err );   // after reset(), which clears it: a cache failure stays latched
        }
    }

    const favoriteSectors = computed< ContentfulSector[] >( () => {
        return allSectors.value.filter( ( sector: ContentfulSector ) => userStore.isFavoriteSector( sector.id ) );
    } );

    const selectedSector = computed< SectorMarketState | undefined >( () => selectedSectorDatePoint.value );

    function sectorBySymbol( symbol: string ): ContentfulSector | undefined {
        return sectorsBySymbol.get( symbol );
    }

    function sectorByTmiId( id: number ): ContentfulSector | undefined {
        return sectorsByTmiId.get( id );
    }

    function sectorById( id: string ): ContentfulSector | undefined {
        return sectorsByContentfulId.get( id );
    }

    function setSelectedSector( sectorHistory: SectorMarketState ) {
        selectedSectorDatePoint.value = sectorHistory;
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,
        sectorById,
        sectorByTmiId,
        sectorBySymbol,
        setSelectedSector,

        // GETTERS
        allSectors,
        favoriteSectors,
        isLoading,
        rsHistory,
        selectedSector
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSectorsStore, import.meta.hot ) );
}